/**
 * Forward / reverse geocoder backed by Photon (https://photon.komoot.io),
 * the same free OpenStreetMap-based service the helipass Android app uses.
 *
 * No API key. Photon only asks for a sensible ``User-Agent`` — browsers
 * already set one. Calls are debounceable; the caller is responsible for
 * not spamming the public service on every keystroke.
 *
 * Used by the DPP rules editor: operators type a civic address, see
 * suggestions, click to place a geofence on the map.
 */

const FORWARD_URL = "https://photon.komoot.io/api/"
const REVERSE_URL = "https://photon.komoot.io/reverse"

export interface PhotonSuggestion {
  /** "1234 Main St" — primary line for the autocomplete row. */
  primary: string
  /** "Montreal, Quebec, Canada" — secondary descriptor. */
  secondary: string
  lat: number
  lon: number
}

interface PhotonProperties {
  name?: string
  housenumber?: string
  street?: string
  city?: string
  state?: string
  country?: string
  postcode?: string
  type?: string
}

interface PhotonFeature {
  geometry?: { coordinates?: [number, number] }
  properties?: PhotonProperties
}

interface PhotonResponse {
  features?: PhotonFeature[]
}

function buildPrimary(p: PhotonProperties): string {
  if (p.housenumber && p.street) return `${p.housenumber} ${p.street}`
  if (p.street) return p.street
  if (p.name) return p.name
  if (p.postcode) return p.postcode
  return ""
}

function buildSecondary(p: PhotonProperties): string {
  const parts: string[] = []
  if (p.city) parts.push(p.city)
  if (p.state) parts.push(p.state)
  if (p.country) parts.push(p.country)
  return parts.join(", ")
}

function parseFeatures(data: PhotonResponse, limit: number): PhotonSuggestion[] {
  const out: PhotonSuggestion[] = []
  for (const feat of data.features ?? []) {
    const coords = feat.geometry?.coordinates
    if (!coords || coords.length < 2) continue
    const [lon, lat] = coords
    if (typeof lat !== "number" || typeof lon !== "number") continue
    const props = feat.properties ?? {}
    const primary = buildPrimary(props)
    if (!primary) continue
    out.push({
      primary,
      secondary: buildSecondary(props),
      lat,
      lon,
    })
    if (out.length >= limit) break
  }
  return out
}

export async function photonSearch(
  query: string,
  options: { limit?: number; signal?: AbortSignal } = {},
): Promise<PhotonSuggestion[]> {
  const trimmed = query.trim()
  if (trimmed.length < 3) return []
  const limit = Math.min(Math.max(options.limit ?? 6, 1), 20)
  const url = `${FORWARD_URL}?q=${encodeURIComponent(trimmed)}&limit=${limit}`
  try {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: options.signal,
    })
    if (!res.ok) return []
    const data = (await res.json()) as PhotonResponse
    return parseFeatures(data, limit)
  } catch {
    return []
  }
}

export async function photonReverse(
  lat: number,
  lon: number,
  options: { signal?: AbortSignal } = {},
): Promise<string | null> {
  const url = `${REVERSE_URL}?lat=${lat.toFixed(6)}&lon=${lon.toFixed(6)}`
  try {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: options.signal,
    })
    if (!res.ok) return null
    const data = (await res.json()) as PhotonResponse
    const first = parseFeatures(data, 1)[0]
    if (!first) return null
    return first.secondary ? `${first.primary}, ${first.secondary}` : first.primary
  } catch {
    return null
  }
}
